import * as bootstrap from "bootstrap";
import { openDownloadUrl } from "util/openDownloadUrl";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { Field, useFormikContext } from "formik";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import { Autofill } from "../AutoFill/autofill";
import { Message } from "./helper";
import { FormUploadDocuments } from "components/FormUploadDocuments";
import SuggestedDocument from "components/documents/suggestedDocument/suggestedDocument";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import { FilingEmailInput } from "./FilingEmailInput";
import { DoBooksWithInkle } from "./DoBooksWithInkle";
import { FilingDirectors } from "./FilingDirectors";
import { ADDRESS_AUTOFILL_VERSION_2_0 } from "constants/task";
import { SelectAutofill } from "./SelectAutofill";
import { AddBankAccount } from "./AddBankAccount";
import { AddFilingManualBank } from "./AddFilingManualBank";
import { AddEditAddress } from "./AddEditAddress";
import ToolTip from "components/design/toolTip";
import { SuggestedDocumentDND } from "./SuggestedDocumentDND";
import { AddEditPersona } from "./AddEditPersona";
import { AddShareholder } from "components/Entity/Shareholders/AddShareholder";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";
import { FilingAuthorizedSignatory } from "./FilingAuthorizedSignatory";
import { FilingPrincipalOfficer } from "./FilingPrincipalOfficer";
import { AddFinancialClosing } from "./FinancialClosing";
import { TaskConnectBank } from "components/TaskConnectBank";
import Radio from "./FilingRadioGroup";
import FilingInfoIcon from "static/images/FilingInfo.svg";
import { YYYY_MM_DD } from "constants/date";
import { AnnualCatchup } from "./AnnualCatchup";
import { AnnualBooksCalculate } from "./AnnualBooksCalculate";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import {
  useGetTaskListForServiceTeamQuery,
  useGetTaskListForUserQuery,
} from "store/apis/taskList";
import { useExportFilingFormMutation } from "store/apis/task";
import { useToast } from "hooks/useToast";
import { getIn } from "formik";
import { PriceInput } from "components/PriceInput/PriceInput";
import LinkedFilings from "./LinkedFilings";
import classNames from "classnames";
import { DISPLAY_DATE } from "constants/date";
import { ShareholderAutofillV1 } from "./ShareholderAutofillV1";
import { ShareholderAutofillV2 } from "./ShareholderAutofillV2";
import { NonUsEntitySelect } from "./NonUsEntitySelect";
import { NumericInput } from "components/NumericInput/NumericInput";
import { SelectBankAutofill } from "./SelectBankAutofill";
import { BusinessFinancialTable } from "./BusinessFinancialTable";
import { BusinessFinancialsField } from "./BusinessFinancialsField";
import { EstimatedTaxField } from "./EstimatedTaxField";
import {
  BUSINESS_FINANCIALS_FIELD_TYPE,
  ESTIMATED_TAX_FIELD_TYPE,
} from "constants/estimatedTaxSections";
import { OpenEye } from "components/icons/OpenEye";
import { ClosedEye } from "components/icons/ClosedEye";
import { useModal } from "hooks/useModal";
import { ConditionalLink } from "components/conditionalLink";
import { DownloadIcon } from "components/icons/Download";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { useParams } from "react-router-dom";
import { FileForm6765, FilingChoiceField } from "./FileForm6765/FileForm6765";
import { LaunchedRAndDFilingView } from "./LaunchedRAndDFilingView";
import { AccessRequestStatus } from "./AccessRequestStatus";
import { BooksHandoffSummary } from "./BooksHandoffSummary";
import { UploadOrLinkField } from "./UploadOrLinkField";
import { hasSubSections } from "util/booksHandoff/hasSubSections";
import { FIELD_TYPE } from "types/Models/Filing";

dayjs.extend(customParseFormat);

const CombinedComponents = (props) => {
  return (
    <>
      <AddBankAccount {...props} />
      <AddFilingManualBank {...props} />
    </>
  );
};

export const FormTooltip = ({ tooltipText }) => {
  const ref = useRef();
  useEffect(() => {
    if (ref.current) {
      new bootstrap.Tooltip(ref.current);
    }
  }, []);
  return (
    <ToolTip
      text={
        <span
          className="!t-text-left"
          dangerouslySetInnerHTML={{ __html: tooltipText }}
        ></span>
      }
      side="right"
      className="!t-text-left !t-max-w-72"
    >
      <img src={FilingInfoIcon} alt="info" className="t-ml-1" />
    </ToolTip>
  );
};

export const Label = ({ label, tooltip, required, name }) => {
  return (
    <Form.Label htmlFor={name} className="m-0 !t-mb-1.5">
      <span
        className="dangerLabel t-font-sans t-text-body-sm t-text-text-30"
        dangerouslySetInnerHTML={{ __html: label }}
      ></span>
      {tooltip && <FormTooltip tooltipText={tooltip} />}
      {required && <span className="t-text-red t-ml-1">*</span>}
    </Form.Label>
  );
};

const CustomField = ({ name, onChange, component: Component, ...props }) => {
  return (
    <Field name={name} {...props}>
      {({
        field, // { name, value, onChange, onBlur }
        form: { touched, errors },
      }) => {
        const hasError = getIn(errors, field.name);
        return (
          <>
            <Component
              {...props}
              {...field}
              onChange={(e) => {
                onChange(e);
                field.onChange(e);
              }}
              className={classNames({
                "t-border-red": hasError,
              })}
            />
            {hasError && (
              <span className="t-text-body-sm t-text-red t-mt-[6px]">
                Please enter a value
              </span>
            )}
          </>
        );
      }}
    </Field>
  );
};

const RdTemplateDownloadButton = () => {
  const { taskId } = useParams();
  const [exportFilingForm, { isLoading }] = useExportFilingFormMutation();
  const { alertToast } = useToast();

  const handleDownload = async () => {
    try {
      const res = await exportFilingForm({
        taskId,
        export_to_email: false,
      }).unwrap();
      if (res?.download_url) {
        openDownloadUrl(res.download_url);
      }
    } catch (error) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  return (
    <div className="t-mb-3">
      <Button
        size="small"
        customType="secondary"
        isLoading={isLoading}
        disabled={isLoading}
        onClick={handleDownload}
      >
        <span className="t-flex t-items-center t-gap-1.5">
          <DownloadIcon />
          Download form
        </span>
      </Button>
    </div>
  );
};

export const FormControl = ({
  authtoken,
  type,
  label,
  options = [],
  tooltip,
  descript,
  defaultValue,
  mandatory,
  current,
  data,
  uuid,
  groupId,
  autofillKey,
  autofillPrefix,
  autofillSuffix,
  updatedTask,
  formId,
  document,
  sectionKey,
  tag,
  field,
  isArchived,
  entityId,
  section,
  formData,
  fields,
  season,
  quarter,
  deadline,
  selectedCurrency,
  entityName,
  ...props
}) => {
  const { isCustomer, isAdmin, isAnyServiceUser } = useRoleBasedView();
  const { values, setFieldValue, errors } = useFormikContext();
  const fieldError =
    typeof getIn(errors, field.field_key) === "string"
      ? getIn(errors, field.field_key)
      : undefined;
  const { total_revenue, total_expense, r_and_d_expense = 0 } = values;
  const showPassword = useModal();

  const fieldValue = values[props.name] || defaultValue;
  const [localOptions, setLocalOptions] = useState(
    options.map((option) => ({
      label: option,
      value: option,
    }))
  );

  useEffect(() => {
    setLocalOptions((prevOptions) => {
      if (
        fieldValue &&
        !prevOptions.some((option) => option.value === fieldValue)
      ) {
        return [...prevOptions, { label: fieldValue, value: fieldValue }];
      }
      return prevOptions;
    });
  }, [localOptions, fieldValue]);

  const netProfit = useMemo(() => {
    if (!total_revenue || !total_expense) {
      return null;
    }

    return (
      Number(total_revenue) -
      (Number(total_expense) - Number(r_and_d_expense)) -
      Number(r_and_d_expense) / 15
    );
  }, [total_revenue, total_expense, r_and_d_expense]);

  const {
    data: userTaskData,
    isLoading: isUserTaskLoading,
    isFetching: isUserTaskFetching,
  } = useGetTaskListForUserQuery(
    {
      baseTaskKey: field.control_basetask?.join(","),
      seasons: season,
      showOngoing: true,
    },
    {
      skip:
        isAnyServiceUser ||
        !field.control_basetask ||
        !season ||
        field.control_basetask?.length === 0,
    }
  );

  const {
    data: cpaTaskList,
    isLoading: isCPATaskLoading,
    isFetching: isAdminTaskFetching,
  } = useGetTaskListForServiceTeamQuery(
    {
      baseTaskKey: field.control_basetask?.join(","),
      groupId,
      seasons: season,
      showOngoing: true,
    },
    {
      skip:
        isCustomer ||
        !groupId ||
        !season ||
        !field.control_basetask ||
        field.control_basetask?.length === 0,
    }
  );

  const count =
    (userTaskData || cpaTaskList)?.tasks?.filter(
      (task) => task.entity?.uuid === entityId
    )?.length || 0;

  if (isUserTaskLoading || isCPATaskLoading) {
    return (
      <div className="t-bg-neutral-10 t-w-full t-my-2 t-h-5 t-animate-pulse"></div>
    );
  }

  if (
    (field.control_basetask_for === "CUSTOMER" && isCustomer) ||
    (field.control_basetask_for === "ADMIN" && isAdmin) ||
    !field.control_basetask_for
  ) {
    if (field.control_basetask && field.control_basetask.length > 0) {
      if (field.control_basetask_show && count == 0) {
        return <></>;
      }
      if (!field.control_basetask_show && count > 0) {
        return <></>;
      }
    }
  }

  if (field.should_show_on_profit && (!netProfit || netProfit < 0)) {
    return <></>;
  }

  const isDocumentUploaded = (uploadedDocs, suggestedDoc) => {
    return uploadedDocs?.some(
      (uploadedDoc) => uploadedDoc?.uuid === suggestedDoc?.uuid
    );
  };

  if (
    (field.is_hidden_for_customer && isCustomer) ||
    (field.is_hidden_for_admin && !isCustomer)
  ) {
    return null;
  }

  const year = season ? season : dayjs().year() - 1;

  if (type === "action") {
    switch (field.cta.cta_action) {
      case "open_bookkeeping_subscriptions": {
        return (
          <DoBooksWithInkle
            field={field}
            groupId={groupId}
            entityId={entityId}
          />
        );
      }
      case "add_bank_account": {
        return (
          <TaskConnectBank
            entityId={entityId}
            groupId={groupId}
            openAccordionByDefault={false}
            date={dayjs(`${year}-12-31`).format(YYYY_MM_DD)}
          />
        );
      }

      case "add_view_financials": {
        return <AddFinancialClosing />;
      }

      default: {
        return <></>;
      }
    }
  }

  let autofillKeys = [];

  if (autofillKey && !autofillSuffix)
    autofillKeys = section.section_fields?.reduce((ac, cv) => {
      if (cv.autofill_section === field.autofill_section && cv.field_key) {
        const fieldKeyWithoutPrefix = cv.field_key?.replace(
          `${field.autofill_prefix}_`,
          ""
        );
        if (fieldKeyWithoutPrefix) {
          return [...ac, fieldKeyWithoutPrefix];
        }
        return ac;
      }
      return ac;
    }, []);

  if (autofillKey && autofillSuffix) {
    autofillKeys = section.repeatable_fields
      ?.find(({ id }) => id == autofillSuffix)
      ?.fields?.reduce((ac, cv) => {
        if (cv.autofill_section === field.autofill_section && cv.field_key) {
          const fieldKeyWithoutPrefix = cv.field_key?.replace(
            `${field.autofill_prefix}_`,
            ""
          );
          if (fieldKeyWithoutPrefix) {
            return [...ac, fieldKeyWithoutPrefix];
          }
          return ac;
        }
        return ac;
      }, []);
  }

  const repeatableFieldData =
    section?.repeatable_fields?.find(({ id }) => id == autofillSuffix) || {};

  if (type === "business_financial_table") {
    return (
      <BusinessFinancialTable
        headers={field.headers}
        data={field.data}
        entityName={entityName}
        entityId={entityId}
      />
    );
  }

  if (type === BUSINESS_FINANCIALS_FIELD_TYPE) {
    return (
      <BusinessFinancialsField
        data={field.data}
        entityId={entityId}
        year={season}
        quarter={quarter}
      />
    );
  }

  if (type === ESTIMATED_TAX_FIELD_TYPE) {
    return (
      <EstimatedTaxField
        data={field.data}
        entityId={entityId}
        groupId={groupId}
        season={season}
      />
    );
  }

  if (type === "annual_catchup_subscription") {
    return (
      <AnnualCatchup
        season={season}
        entityIdFromProps={entityId}
        taskCount={(userTaskData || cpaTaskList)?.count || 0}
        controlBasetask={field.control_basetask?.join(",")}
        isFetching={isUserTaskFetching || isAdminTaskFetching}
      />
    );
  }

  if (type === "launched_r_and_d_filing") {
    return <LaunchedRAndDFilingView taskId={field.value} />;
  }
  if (type === "filing_choice") {
    return (
      <FilingChoiceField
        season={season}
        entityId={entityId}
        setFieldValue={setFieldValue}
        values={values}
        onRadioChange={props.onRadioChange}
      />
    );
  }
  if (type === "fpt_disqualified_banner") {
    return (
      <Banner
        type="warning"
        description="These responses indicate that your work might not be eligible for the R&D credit. This is a brief initial review, and your Inkle tax expert can analyze it more thoroughly if you'd like."
      />
    );
  }
  if (type === "file_form_6765") {
    return <FileForm6765 season={season} entityId={entityId} />;
  }

  if (type === "annual_books_calculation") {
    return <AnnualBooksCalculate deadline={deadline} />;
  }

  if (type === FIELD_TYPE.BOOKS_HANDOFF_SUMMARY) {
    return <BooksHandoffSummary data={field.data} />;
  }

  if (type === FIELD_TYPE.ACCESS_REQUEST_STATUS) {
    return <AccessRequestStatus data={field.data} />;
  }

  if (type === "form_5471_and_5472") {
    return <LinkedFilings season={season} entityId={entityId} />;
  }

  if (
    section?.version == "2" &&
    sectionKey === "shareholders" &&
    type === "select_shareholder_autofill"
  ) {
    return (
      <ShareholderAutofillV2
        section={section}
        groupId={groupId}
        entityId={entityId}
        formData={formData}
        field={field}
      />
    );
  }
  if (
    type === "select_shareholder_autofill" &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <ShareholderAutofillV1
        section={section}
        groupId={groupId}
        entityId={entityId}
        formData={formData}
        autofillKey={autofillKey}
        field={field}
        keysToFill={autofillKeys}
        deleteModalTitle="shareholder"
        addNewAutofillText="Add new shareholder"
        comboboxName="shareholder"
        currentAutofillDetailKey="shareholder_details"
        shouldShowDetailsCard={
          autofillSuffix
            ? repeatableFieldData[
                `is_shareholder_selected_for_repeatable_fields_${autofillSuffix}`
              ]
            : section.is_shareholder_selected
        }
        isAnyDetailsMissing={
          autofillSuffix
            ? repeatableFieldData[
                `is_shareholder_data_missing_for_repeatable_fields_${autofillSuffix}`
              ]
            : section.is_shareholder_data_missing
        }
        autofillSuffix={autofillSuffix}
        AutofillCreateEdit={AddShareholder}
        repeatableFieldData={repeatableFieldData}
        fields={fields}
      />
    );
  }

  if (
    type === "select_bank_autofill" &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <SelectBankAutofill
        section={section}
        groupId={groupId}
        entityId={entityId}
        formData={formData}
        autofillKey={autofillKey}
        field={field}
        keysToFill={autofillKeys}
        deleteModalTitle="bank"
        addNewAutofillText="Add new bank"
        comboboxName="bank"
        currentAutofillDetailKey="bank_details"
        shouldShowDetailsCard={
          autofillSuffix
            ? repeatableFieldData[
                `is_bank_account_selected_for_repeatable_fields_${autofillSuffix}`
              ]
            : section.is_bank_account_selected
        }
        isAnyDetailsMissing={
          autofillSuffix
            ? repeatableFieldData[
                `is_bank_account_data_missing_for_repeatable_fields_${autofillSuffix}`
              ]
            : section.is_bank_account_data_missing
        }
        isBankAutofill
        AutofillCreateEdit={CombinedComponents}
        repeatableFieldData={repeatableFieldData}
        fields={fields}
        autofillSuffix={autofillSuffix}
        error={fieldError}
      />
    );
  }

  if (
    type === "select_autofill" &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <SelectAutofill
        section={section}
        groupId={groupId}
        entityId={entityId}
        formData={formData}
        autofillKey={autofillKey}
        field={field}
        keysToFill={autofillKeys}
        deleteModalTitle="address"
        addNewAutofillText="Add new address"
        comboboxName="address"
        currentAutofillDetailKey={
          field.autofill_detail_key || "address_details"
        }
        shouldShowDetailsCard={
          autofillSuffix
            ? repeatableFieldData[
                `is_address_selected_for_repeatable_fields_${autofillSuffix}`
              ]
            : section.is_address_selected
        }
        isAnyDetailsMissing={
          autofillSuffix
            ? repeatableFieldData[
                `is_address_data_missing_for_repeatable_fields_${autofillSuffix}`
              ]
            : section.is_address_data_missing
        }
        isAddressAutofill
        AutofillCreateEdit={AddEditAddress}
        autofillSuffix={autofillSuffix}
        repeatableFieldData={repeatableFieldData}
        fields={fields}
        label={<Label label={label} tooltip={tooltip} name={props.name} />}
        tooltip={tooltip}
        error={fieldError}
      />
    );
  }

  if (
    type === "select_persona_autofill" &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <SelectAutofill
        section={section}
        groupId={groupId}
        entityId={entityId}
        formData={formData}
        autofillKey={autofillKey}
        field={field}
        keysToFill={autofillKeys}
        deleteModalTitle="persona"
        addNewAutofillText="Add new persona"
        comboboxName="persona"
        currentAutofillDetailKey="persona_details"
        shouldShowDetailsCard={
          autofillSuffix
            ? repeatableFieldData[
                `is_persona_selected_for_repeatable_fields_${autofillSuffix}`
              ]
            : section.is_persona_selected
        }
        isAnyDetailsMissing={
          autofillSuffix
            ? repeatableFieldData[
                `is_persona_data_missing_for_repeatable_fields_${autofillSuffix}`
              ]
            : section.is_persona_data_missing
        }
        isPersonaAutofill
        autofillSuffix={autofillSuffix}
        AutofillCreateEdit={AddEditPersona}
        repeatableFieldData={repeatableFieldData}
        fields={fields}
      />
    );
  }

  if (
    type === "select_directors_autofill" &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <FilingDirectors
        section={section}
        groupId={groupId}
        entityId={entityId}
        formData={formData}
        autofillSuffix={autofillSuffix}
        field={field}
        error={fieldError}
      />
    );
  }

  if (
    type === "select_authorized_signatory_autofill" &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <FilingAuthorizedSignatory
        section={section}
        groupId={groupId}
        entityId={entityId}
        formData={formData}
        keysToFill={autofillKeys}
        fields={fields}
        field={field}
        required={field.required}
        error={fieldError}
      />
    );
  }
  if (
    type === "select_principal_officer_autofill" &&
    formData.data.data.address_autofill_version === ADDRESS_AUTOFILL_VERSION_2_0
  ) {
    return (
      <FilingPrincipalOfficer
        section={section}
        groupId={groupId}
        entityId={entityId}
        formData={formData}
        keysToFill={autofillKeys}
        fields={fields}
        field={field}
        error={fieldError}
      />
    );
  }

  if (type === "select_autofill" && authtoken) {
    return (
      <Autofill
        entityId={entityId}
        label={label}
        groupId={groupId}
        autofillKey={autofillKey}
        autofillPrefix={autofillPrefix}
        autofillSuffix={autofillSuffix}
      />
    );
  }

  if (type === "select") {
    const selectedOption = fieldValue
      ? { label: fieldValue, value: fieldValue }
      : null;
    const error = fieldError;

    return (
      <div>
        {label && (
          <Label
            label={label}
            tooltip={tooltip}
            name={props.name}
            required={field.required}
          />
        )}
        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <Combobox
            label=""
            components={{
              ClearIndicator: () => null,
            }}
            placeholder={field.placeholder}
            name={field.name}
            options={localOptions}
            value={selectedOption}
            isDisabled={field.disabled}
            required={field.required}
            creatable={field.creatable}
            onCreateOption={(value) => {
              setFieldValue(field.field_key, value);
              setLocalOptions((prev) => [
                ...prev,
                { label: value, value: value },
              ]);
            }}
            {...props}
            onChange={(option) => setFieldValue(field.field_key, option?.value)}
            error={error}
            type={error && "error"}
          />
          {error && (
            <div className="t-mt-1.5 t-text-body-sm t-text-red t-italic t-font-light">
              {error}
            </div>
          )}
          {field.note && values[field.name].length > 0 && (
            <span className="t-text-caption t-text-yellow-80">
              {field.note}
            </span>
          )}
        </span>
      </div>
    );
  }

  if (type === "multiselect") {
    const selectedOptions =
      defaultValue?.length > 0
        ? options.filter((option) => defaultValue.includes(option.value))
        : null;

    return (
      <div>
        {label && (
          <Label
            label={label}
            tooltip={tooltip}
            name={props.name}
            required={field.required}
          />
        )}
        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <Combobox
            label=""
            components={{
              ClearIndicator: () => null,
            }}
            isMulti
            withForm
            placeholder={props.placeholder}
            name={props.name}
            options={options}
            defaultValue={selectedOptions}
            isDisabled={props.disabled}
            {...props}
          />
          {field.note && values[props.name].length > 0 && (
            <span className="t-text-caption t-text-yellow-80">
              {field.note}
            </span>
          )}
        </span>
      </div>
    );
  }

  if (type === "checkbox") {
    return (
      <div className="d-flex align-items-center gap-3">
        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <div className="d-flex gap-2">
            <CustomField
              name={props.name}
              component={Form.Check}
              type={type}
              defaultChecked={defaultValue}
              title={tooltip}
              {...props}
              label={null}
            />
            <Label
              required={field.required}
              label={label}
              tooltip={tooltip}
              name={props.name}
            />
          </div>
        </span>
        <span
          className="descT last"
          dangerouslySetInnerHTML={{ __html: descript }}
        />
      </div>
    );
  }

  if (type === "radio") {
    return (
      <>
        <span
          className="descT last"
          dangerouslySetInnerHTML={{ __html: descript }}
        />

        {Boolean(descript) && (
          <>
            <br />
            <br />
          </>
        )}

        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}

        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <Radio.Root
            defaultValue={field.value}
            onValueChange={(value) => props.onRadioChange(value)}
          >
            <Radio.Content className="t-mt-2 t-space-y-1">
              {options?.map((ele) => (
                <Radio.Item
                  asChild
                  inline={false}
                  value={ele?.value}
                  key={ele?.value}
                >
                  <div className="t-text-body t-text-text-100 t-w-full t-text-left">
                    {ele?.label}
                  </div>
                </Radio.Item>
              ))}
            </Radio.Content>
          </Radio.Root>
        </span>
      </>
    );
  }

  if (type === "password") {
    return (
      <>
        <span
          className="descT last"
          dangerouslySetInnerHTML={{ __html: descript }}
        />

        {Boolean(descript) && (
          <>
            <br />
            <br />
          </>
        )}

        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}

        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <TextInput
            name={props.name}
            component={Form.Control}
            defaultValue={defaultValue}
            title={tooltip}
            {...props}
            type={showPassword.isOpen ? "text" : "password"}
            rightComponent={
              field.has_access ? (
                <div className="t-cursor-pointer" onClick={showPassword.toggle}>
                  {showPassword.isOpen ? <OpenEye /> : <ClosedEye />}
                </div>
              ) : null
            }
          />
        </span>
        {field.note && (
          <span dangerouslySetInnerHTML={{ __html: field.note }} />
        )}
      </>
    );
  }

  if (type === "text") {
    return (
      <>
        <span
          className="descT last"
          dangerouslySetInnerHTML={{ __html: descript }}
        />

        {Boolean(descript) && (
          <>
            <br />
            <br />
          </>
        )}

        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}

        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <TextInput
            name={props.name}
            component={Form.Control}
            defaultValue={defaultValue}
            title={tooltip}
            type={type}
            {...props}
          />
        </span>
      </>
    );
  }

  if (type === FIELD_TYPE.TEXTAREA) {
    return (
      <>
        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}
        <TextArea
          name={props.name}
          placeholder={field.placeholder}
          disabled={props.disabled}
          rows={4}
        />
      </>
    );
  }

  if (type === "number") {
    return (
      <>
        <span
          className="descT last"
          dangerouslySetInnerHTML={{ __html: descript }}
        />

        {Boolean(descript) && (
          <>
            <br />
            <br />
          </>
        )}

        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}

        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <NumericInput
            fieldProps={{ name: field.field_key }}
            storeNumeric
            numericProps={{
              fixedDecimalScale: true,
              decimalScale: field.decimal_scale || 0,
              allowNegative: field.allow_negative,
              maxLength: field.max_digit_limit || 1000,
              thousandSeparator: field.thousand_separator || "",
              suffix: field.suffix || "",
              placeholder: field.placeholder || "",
            }}
          />
        </span>
        {field.note && (
          <span dangerouslySetInnerHTML={{ __html: field.note }} />
        )}
      </>
    );
  }

  if (type === "amount") {
    return (
      <>
        <span
          className="descT last"
          dangerouslySetInnerHTML={{ __html: descript }}
        />

        {Boolean(descript) && (
          <>
            <br />
            <br />
          </>
        )}

        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}

        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <PriceInput
            name={props.name}
            label=""
            allowNegative
            placeholder="0.00"
            currencyCode={selectedCurrency || "USD"}
          />
        </span>
      </>
    );
  }

  if (type === "phone") {
    return (
      <>
        <span
          className="descT last"
          dangerouslySetInnerHTML={{ __html: descript }}
        />

        {Boolean(descript) && (
          <>
            <br />
            <br />
          </>
        )}

        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}

        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <div>
            <MobileInput name={props.name} title={tooltip} {...props} />
          </div>
        </span>
      </>
    );
  }

  if (type === "email") {
    return (
      <>
        <span
          className="descT last"
          dangerouslySetInnerHTML={{ __html: descript }}
        />

        {Boolean(descript) && (
          <>
            <br />
            <br />
          </>
        )}

        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}

        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <FilingEmailInput
            name={props.name}
            defaultValue={defaultValue}
            title={tooltip}
            type={type}
            {...props}
          />
        </span>
      </>
    );
  }

  // a labelled info field in the books handoff step is a read only answer, existing info fields keep their html
  if (type === FIELD_TYPE.INFO && hasSubSections(section) && label) {
    return (
      <div>
        <Label label={label} tooltip={tooltip} name={props.name} />
        <div className="t-text-body-sm t-text-text-100">{field.value}</div>
      </div>
    );
  }

  if (type === "info") {
    return (
      <span className="t-mb-4" dangerouslySetInnerHTML={{ __html: descript }} />
    );
  }

  if (type === "non_us_entity_select") {
    return (
      <>
        {label && (
          <Label
            required={field.required}
            label={label}
            tooltip={tooltip}
            name={props.name}
          />
        )}
        <span message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <NonUsEntitySelect
            name={props.name}
            required={field.required}
            placeholder={props.placeholder}
            disabled={props.disabled}
            groupId={groupId}
            fieldKey={field.field_key}
          />
        </span>
      </>
    );
  }
  if (type === "date") {
    return (
      <>
        {label && (
          <Label required={field.required} label={label} tooltip={tooltip} />
        )}
        <div message={Message({ current, data, uuid, isArchived })}>
          <span className="msgId">
            {Message({ current, data, uuid, isArchived })}
          </span>
          <Field name={props.name}>
            {({ field }) => {
              const formatDate = (dateString) => {
                if (!dateString) return "";

                const formats = ["DD-MM-YYYY", DISPLAY_DATE, "YYYY-MM-DD"];
                for (const format of formats) {
                  const parsedDate = dayjs(dateString, format, true);
                  if (parsedDate.isValid()) {
                    return parsedDate.format("YYYY-MM-DD");
                  }
                }

                return dateString;
              };

              const formattedDate = formatDate(field?.value);

              return (
                <DateInput
                  {...field}
                  {...props}
                  value={formattedDate}
                  disabled={props.disabled}
                  name={props.name}
                  required={mandatory}
                  portalId="portal"
                  placeholder={DISPLAY_DATE}
                  min="1900-01-01"
                  max="2050-12-31"
                />
              );
            }}
          </Field>
        </div>
      </>
    );
  }

  if (type === FIELD_TYPE.UPLOAD_OR_LINK) {
    return (
      <div className="t-mb-2">
        {field.field_label && (
          <Label
            required={field.required}
            label={field.field_label}
            tooltip={tooltip}
            name={props.name}
          />
        )}
        <UploadOrLinkField
          field={field}
          formId={formId}
          groupId={groupId}
          error={fieldError}
        />
      </div>
    );
  }

  if (type === "file_upload") {
    return (
      <div className="t-mb-2">
        {field.show_rd_template_download && <RdTemplateDownloadButton />}
        {field.field_label && (
          <div className="t-flex t-items-center t-justify-between">
            <Label
              required={field.required}
              label={field.field_label}
              tooltip={tooltip}
              name={props.name}
            />
            {field.template_url && (
              <ConditionalLink to={field.template_url}>
                <button
                  type="button"
                  className="all:unset t-cursor-pointer t-flex t-items-center t-gap-1"
                >
                  <span className="t-text-text-30 t-mb-1.5">
                    <DownloadIcon color="currentColor" />
                  </span>
                  <span className="t-text-text-30 t-text-body-sm t-italic">
                    {field.template_label}
                  </span>
                </button>
              </ConditionalLink>
            )}
          </div>
        )}
        <FormUploadDocuments
          field={{ ...field, field_label: null, label: null }}
          formInstanceId={formId}
          groupId={groupId}
          suggestedDocUpload={() => {}}
          isMulti={!field.allow_single_upload}
          templateUrl={field.template_url}
        />
        {field?.suggested_documents?.length ? (
          <p className="t-my-2 t-text-caption t-text-neutral">
            Suggested Document below. Drag and Drop to attach your files.
          </p>
        ) : null}
        <div className="t-flex t-max-w-[600px] t-flex-wrap t-items-center t-gap-2">
          {field?.suggested_documents?.map((doc) => {
            const isUploaded = isDocumentUploaded(field?.documents, doc);

            return (
              <SuggestedDocumentDND
                isUploaded={isUploaded}
                key={doc?.uuid}
                data={JSON.stringify(doc)}
              >
                <SuggestedDocument
                  document={doc}
                  groupId={groupId}
                  isUploaded={isUploaded}
                />
              </SuggestedDocumentDND>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
};
